import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { MessageSquare, Send, X, Sparkles, LogIn, UserPlus, Compass } from "lucide-react";
import { api, formatErr, cx } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../lib/auth";

const STORAGE_KEY = "concierge_session_v1";

export default function ConciergeWidget({ open, onClose }) {
  const { t } = useI18n();
  const { user } = useAuth();
  const [signupGrant, setSignupGrant] = useState(0);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem(STORAGE_KEY) || null);
  const [messages, setMessages] = useState([
    { role: "assistant", content: t("concierge.welcome", "Welcome. I'm your Meet & Mint Concierge. Tell me what you're looking for in Ibiza — a sunset yacht, a cliffside villa, a VIP table — and I'll start curating.") },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const scroller = useRef(null);

  useEffect(() => {
    if (!open) return;
    api.get("/settings/public").then(({ data }) => setSignupGrant(data.mint_signup_grant || 0)).catch(() => {});
  }, [open]);

  useEffect(() => {
    if (scroller.current) scroller.current.scrollTop = scroller.current.scrollHeight;
  }, [messages, open, busy]);

  useEffect(() => {
    if (!open || !sessionId || !user) return;
    (async () => {
      try {
        const { data } = await api.get(`/concierge/history/${sessionId}`);
        if (data.items?.length) {
          setMessages([
            { role: "assistant", content: t("concierge.welcome_back", "Welcome back. Continuing where we left off.") },
            ...data.items.map((m) => ({ role: m.role, content: m.content })),
          ]);
        }
      } catch {/*ignore*/}
    })();
  }, [open, sessionId, t, user]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setError(null);
    setMessages((m) => [...m, { role: "user", content: text }]);
    setInput("");
    setBusy(true);
    try {
      const { data } = await api.post("/concierge/chat", {
        message: text,
        session_id: sessionId || undefined,
      });
      if (data.session_id) {
        setSessionId(data.session_id);
        localStorage.setItem(STORAGE_KEY, data.session_id);
      }
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (e) {
      setError(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      {/* Floating button — circle by default, expands on hover to show "Concierge" label */}
      {!open && (
        <button
          onClick={() => onClose(false) /* meaning open */}
          className="fixed bottom-8 right-8 md:bottom-10 md:right-10 z-50 group flex items-center gap-2 rounded-full bg-teal text-ink shadow-glow hover:bg-teal-hover transition-all duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] h-14 w-14 hover:w-[168px] hover:pr-5 overflow-hidden"
          data-testid="btn-concierge-fab"
          aria-label={t("concierge.heading", "Concierge")}
        >
          <span className="shrink-0 h-14 w-14 grid place-items-center">
            <Sparkles size={20} />
          </span>
          <span className="font-medium tracking-wide text-sm whitespace-nowrap opacity-0 max-w-0 group-hover:opacity-100 group-hover:max-w-[120px] transition-all duration-300 delay-75">
            {t("concierge.heading", "Concierge")}
          </span>
        </button>
      )}

      {/* Panel */}
      {open && (
        <div className="fixed inset-0 md:inset-auto md:bottom-8 md:right-8 lg:bottom-10 lg:right-10 z-50 md:w-[420px] md:h-[640px] flex md:rounded-3xl overflow-hidden glass-solid shadow-ink">
          <div className="flex-1 flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b hairline">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-full bg-teal/15 grid place-items-center text-teal">
                  <Sparkles size={16}/>
                </div>
                <div>
                  <div className="font-script text-bone text-[15px] leading-none">Meet &amp; Mint</div>
                  <div className="eyebrow mt-1">{t("concierge.heading", "Concierge")}</div>
                </div>
              </div>
              <button onClick={() => onClose(true)} className="text-muted hover:text-bone" data-testid="btn-concierge-close" aria-label="Close concierge">
                <X size={18}/>
              </button>
            </div>

            <div ref={scroller} className="flex-1 overflow-auto px-4 py-5 space-y-3" data-testid="concierge-messages">
              {!user ? (
                <ConciergeAuthGate t={t} grant={signupGrant} onClose={onClose} />
              ) : (
                <>
                  {messages.map((m, i) => (
                    <div key={i} className={cx("flex", m.role === "user" ? "justify-end" : "justify-start")}>
                      <div className={cx("max-w-[80%] px-4 py-3 text-sm leading-relaxed",
                        m.role === "user" ? "bubble-user" : "bubble-ai")}>
                        {m.content}
                      </div>
                    </div>
                  ))}
                  {busy && (
                    <div className="flex justify-start">
                      <div className="bubble-ai px-4 py-3 text-sm flex items-center gap-2">
                        <span className="w-1.5 h-1.5 bg-teal rounded-full animate-pulse"/>
                        <span className="w-1.5 h-1.5 bg-teal rounded-full animate-pulse" style={{ animationDelay: "0.15s" }}/>
                        <span className="w-1.5 h-1.5 bg-teal rounded-full animate-pulse" style={{ animationDelay: "0.3s" }}/>
                      </div>
                    </div>
                  )}
                  {error && <div className="text-rose-300 text-xs px-2">{error}</div>}
                </>
              )}
            </div>

            {user && (
              <div className="border-t hairline px-3 py-3 flex items-end gap-2">
                <textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={onKey}
                  rows={1}
                  placeholder={t("concierge.placeholder", "A villa for 8 in Ibiza, sunset Friday…")}
                  className="input-bare resize-none max-h-32"
                  data-testid="concierge-input"
                />
                <button onClick={send} disabled={busy || !input.trim()} className="btn btn-primary !py-2 !px-3 disabled:opacity-50" data-testid="btn-concierge-send" aria-label="Send">
                  <Send size={16}/>
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

function ConciergeAuthGate({ t, grant, onClose }) {
  return (
    <div className="h-full flex flex-col items-stretch gap-5 px-2 py-4" data-testid="concierge-auth-gate">
      <div className="text-center">
        <div className="mx-auto h-14 w-14 rounded-full bg-teal/15 grid place-items-center text-teal mb-3">
          <Sparkles size={22} />
        </div>
        <h3 className="font-serif-display text-2xl text-bone leading-tight">
          {t("concierge_gate.title", "Sign in to your concierge.")}
        </h3>
        <p className="text-sm text-muted mt-2 max-w-xs mx-auto">
          {t("concierge_gate.body", "Create a free account or sign in to chat with your private AI concierge — and unlock booking, MINT rewards and saved trips.")}
        </p>
      </div>

      <ul className="space-y-2.5 px-2">
        {grant > 0 && (
          <li className="flex items-start gap-2.5 text-sm text-bone/85" data-testid="concierge-gate-perk-grant">
            <span className="mt-0.5 h-1.5 w-1.5 rounded-full bg-champagne shrink-0" />
            <span>{t("concierge_gate.perk_grant", "{{grant}} MINT free on signup — covers your first concierge conversations.", { grant })}</span>
          </li>
        )}
        <li className="flex items-start gap-2.5 text-sm text-bone/85">
          <span className="mt-0.5 h-1.5 w-1.5 rounded-full bg-teal shrink-0" />
          <span>{t("concierge_gate.perk_save", "Save trips, reuse preferences, talk to a human concierge anytime.")}</span>
        </li>
      </ul>

      <div className="grid grid-cols-2 gap-2 px-2">
        <Link to="/login" onClick={() => onClose(true)} className="btn btn-primary text-[13px]" data-testid="concierge-gate-login">
          <LogIn size={14} /> {t("concierge_gate.sign_in_btn", "Sign in")}
        </Link>
        <Link to="/signup" onClick={() => onClose(true)} className="btn btn-outline text-[13px]" data-testid="concierge-gate-signup">
          <UserPlus size={14} /> {t("concierge_gate.sign_up_btn", "Create account")}
        </Link>
      </div>
      <Link
        to="/destinations/ibiza-spain"
        onClick={() => onClose(true)}
        className="text-center text-xs text-muted hover:text-teal underline-offset-4 hover:underline px-2"
        data-testid="concierge-gate-explore"
      >
        <Compass size={12} className="inline-block mr-1.5" />
        {t("concierge_gate.explore_btn", "Explore the catalog while you decide")}
      </Link>
    </div>
  );
}
